<template>
  <div>
    <div class="mb-0.5 pl-1 text-xs">
      {{ name }}
    </div>
    <v-select
      v-model="selected"
      :options="optionList"
      :placeholder="placeholder || t('plz_select')"
      :clearable="clearable"
      :multiple="multiple"
      :disabled="disabled"
      :searchable="searchable"
      :reduce="reduceOption"
      :selectable="selectable"
      :append-to-body="appendToBody"
      label="text"
      value="value"
      class="w-full"
      @search="onSearch"
    >
      <template #selected-option="item">
        <span v-if="item?.value">
          {{ item.text }}
        </span>
        <span v-else> {{ t('no_data_search') }} </span>
      </template>
      <template #no-options>
        <div class="pb-1 text-sm">{{ t('no_data') }}</div>
      </template>
      <template #open-indicator="{ attributes }">
        <div v-bind="attributes" class="mx-2">
          <icon name="ic_common_arrow01_down" class="h-4 w-4" />
        </div>
      </template>
      <template #list-footer>
        <div v-if="props.lazyLoad" class="pagination">
          <button
            :disabled="offset < 0"
            class="disabled-btn w-1/2 p-1.5 text-xs duration-300 hover:bg-cdp-main-primary-light"
            @click="(offset -= limit), getApi()"
          >
            {{ t('previous_page') }}
          </button>
          <button
            :disabled="offset > count"
            class="disabled-btn w-1/2 p-1.5 text-xs duration-300 hover:bg-cdp-main-primary-light"
            @click="(offset += limit), getApi()"
          >
            {{ t('next_page') }}
          </button>
        </div>
      </template>
    </v-select>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import axios from '@/plugins/axios/https';

import vSelect from 'vue-select';

import { ref, computed, defineComponent, watch } from 'vue';
export default defineComponent({
  /**
   * 最初註冊名稱與檔案名稱開頭大小寫不一致，故針對此命名（Histoire 讀檔用）
   */
  name: 'Selects',
  components: {
    vSelect
  },
  props: {
    name: {
      type: String,
      default: ''
    },
    modelValue: {
      type: [String, Number, Array],
      default: null
    },
    items: {
      type: Array,
      default: () => [
        {
          text: String,
          value: Number
        }
      ]
    },
    header: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: true
    },
    selectable: {
      type: Function,
      default: () => true
    },
    lazyLoad: {
      type: Boolean,
      default: false
    },
    fetchApi: {
      type: String,
      default: ''
    },
    idField: {
      type: String,
      default: ''
    },
    nameField: {
      type: String,
      default: ''
    },
    appendToBody: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const show = ref(false);
    const offset = ref(0);
    const limit = ref(5);
    const count = ref(0);
    const ajaxData = ref([]);
    const tempListData = ref([]);

    const optionList = computed(() => (props.lazyLoad ? ajaxData.value : props.items));

    const selected = computed({
      get: () => props.modelValue,
      set: (val) => {
        emit('update:modelValue', val);
        emit('change', val);
      }
    });

    function reduceOption(option) {
      return option.value;
    }

    watch(
      () => props.lazyLoad,
      async (lazyLoad) => {
        if (lazyLoad) {
          await getApi();
        }
      },
      { immediate: true }
    );

    async function getApi(search = '') {
      const params = {
        search,
        offset: offset.value,
        limit: limit.value
      };

      // make sure the selected value is in the list
      if (selected.value) {
        const tempParams = {
          search,
          offset: offset.value,
          limit: limit.value
        };
        tempParams.uuid = selected.value;
        const data = await axios.get(`${props.fetchApi}`, tempParams, {
          loading: false
        });

        if (!data.msg) {
          count.value = data?.count;
          tempListData.value = data?.results.map((item) => ({
            text: item[props.nameField],
            value: item[props.idField]
          }));
        }
      }

      if (props.multiple) {
        if (selected.value?.length > 0) {
          selected.value.forEach((key) => {
            const hasKey = tempListData.value.some((item) => item.value === key);
            if (!hasKey) {
              const data = ajaxData.value.find((item) => item.value === key);
              if (data) {
                tempListData.value.push(data);
              }
            }
          });
        }
      } else {
        if (selected.value?.length > 0) {
          const data = ajaxData.value.find((item) => item.value === selected.value);
          if (data) {
            tempListData.value = [data];
          }
        }
      }

      const data = await axios.get(`${props.fetchApi}`, params, {
        loading: false
      });

      if (!data.msg) {
        count.value = data?.count;
        ajaxData.value = data?.results.map((item) => ({
          text: item[props.nameField],
          value: item[props.idField]
        }));

        if (tempListData.value?.length > 0) {
          ajaxData.value = ajaxData.value.concat(tempListData.value);

          // remove duplicate data
          ajaxData.value = [
            ...new Set(ajaxData.value.map((obj) => JSON.stringify(obj)))
          ].map((str) => JSON.parse(str));
        }
      }
    }

    async function onSearch(search, loading) {
      if (props.lazyLoad) {
        loading(true);
        await getApi(search);
        loading(false);
      }
    }

    return {
      props,
      t,
      show,
      count,
      offset,
      limit,
      optionList,
      reduceOption,
      selected,
      onSearch,
      getApi
    };
  }
});
</script>